import {
    isWholeProjectOnlyProject,
    ProjectType
} from "in_queue/types/projectType";
import { MisoPercentageReduction } from "in_queue/types/scenarioType";
import { Loading, mapLoadingStateWithFallback } from "types/loadingType";

// The fields `isWholeProjectOnlyProject` reads, plus the id. Structural so that
// QueueProject, SpeculativeProject and the Project union all satisfy it.
type IdentifiableProject = {
    projectId: string;
    type: ProjectType;
    metadata?: { [key: string]: string } | null;
};

// Ids of the whole-project-only projects in `projects`. Takes a project list
// straight off its context, loading state and all -- empty while it loads --
// so every caller is one line.
export const getWholeProjectOnlyProjectIds = (
    projects: Loading<ReadonlyArray<IdentifiableProject>>
): Set<string> =>
    mapLoadingStateWithFallback(
        projects,
        (loaded) =>
            new Set(
                loaded.filter(isWholeProjectOnlyProject).map((p) => p.projectId)
            ),
        new Set<string>()
    );

// A whole-project-only project is studied in service or dropped, never derated,
// so the only two reductions it can carry are 0/0 and 100/100. Anything else --
// a partial percentage, or an asymmetric ERIS/NRIS split -- is a state the ISO
// does not study, and the pipeline rejects it on the run's first asset.
export const isWholeProjectReduction = (
    assumption: MisoPercentageReduction
): boolean =>
    assumption.eris === assumption.nris &&
    (assumption.eris === 0 || assumption.eris === 100);

// Strip any unsupported reduction on a whole-project-only project and report
// which projects were affected. Dropping the assumption leaves the project in
// service, which is the conservative read: a 20% request cannot be honoured, and
// rounding it to a full drop would remove a project the user asked to keep.
export const dropPartialWholeProjectOnlyAssumptions = (
    assumptions: Record<string, MisoPercentageReduction>,
    wholeProjectOnlyProjectIds: Set<string>
): {
    assumptions: Record<string, MisoPercentageReduction>;
    droppedProjectIds: string[];
} => {
    const next = { ...assumptions };
    const droppedProjectIds: string[] = [];
    for (const projectId of Object.keys(next)) {
        if (
            wholeProjectOnlyProjectIds.has(projectId) &&
            !isWholeProjectReduction(next[projectId])
        ) {
            delete next[projectId];
            droppedProjectIds.push(projectId);
        }
    }
    return { assumptions: next, droppedProjectIds };
};
